import {
  Card,
  CardBody,
  DefinitionList,
  DefinitionListItem,
  DefinitionTerm,
  DefinitionValue,
  SectionHeader,
} from "@apex/component-library";
import { Box, SimpleGrid, Text } from "@chakra-ui/react";

import { useGetAccountDetails } from "../../../api/useGetAccountDetails";
import useGetCustomerSummary from "../../../api/useGetCustomerSummary";
import useGetLastProcessDate from "../../../api/useGetLastProcessDate";
import { useGetMargins } from "../../../api/useGetMargins";
import { useAccountLookupStore } from "../../../hooks/useAccountLookupStore";
import { slugify } from "../../../utils/stringFormatters";
import { getErrorText } from "../../CallAlertsAuxWindow/auxUtils";
import SkeletonDefinitionList from "../../SkeletonDefinitionList";
import { buildAccountSummary } from "./utils/buildAccountSummary";
import { buildCustomerSummary } from "./utils/buildCustomerSummary";

function SummaryTab() {
  const accountNumber = useAccountLookupStore(state => state.accountNumber);

  const { data: accountDetails, error: accountDetailsError } =
    useGetAccountDetails(accountNumber);

  const { data: marginAccountData, isLoading: isMarginAccountLoading } =
    useGetMargins(accountNumber);

  const { data: lastProcessDate } = useGetLastProcessDate({
    enabled: !!accountNumber,
  });

  const processDate = lastProcessDate || "";

  const {
    data: customerSummaryData,
    isLoading: isCustomerSummaryLoading,
    error: customerSummaryError,
  } = useGetCustomerSummary(accountNumber, processDate);

  const customerSummary =
    customerSummaryData && accountDetails
      ? buildCustomerSummary(
          customerSummaryData,
          accountDetails,
          accountDetailsError
        )
      : [];

  const accountSummary = marginAccountData
    ? buildAccountSummary(marginAccountData)
    : [];
  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
      <Box>
        <Card>
          <SectionHeader>Account</SectionHeader>
          <CardBody>
            {isMarginAccountLoading ? (
              <Box py={2}>
                <SkeletonDefinitionList count={7} />
              </Box>
            ) : (
              <DefinitionList>
                {accountSummary.map(item => (
                  <DefinitionListItem
                    _last={{
                      borderBottomWidth: 0,
                    }}
                    data-testid={`definition-${slugify(String(item.label))}`}
                  >
                    <DefinitionTerm>{item.label}</DefinitionTerm>
                    <DefinitionValue>
                      <Box w="50%" textAlign="right">
                        {item.value}
                      </Box>
                    </DefinitionValue>
                  </DefinitionListItem>
                ))}
              </DefinitionList>
            )}
          </CardBody>
        </Card>
      </Box>
      <Box>
        <Card>
          <SectionHeader>Customer</SectionHeader>
          <CardBody>
            {isCustomerSummaryLoading ? (
              <Box py={2}>
                <SkeletonDefinitionList count={11} />
              </Box>
            ) : (
              <DefinitionList>
                {customerSummary.map(item => (
                  <DefinitionListItem
                    _last={{
                      borderBottomWidth: 0,
                    }}
                    data-testid={`definition-${slugify(String(item.label))}`}
                    key={item.label}
                  >
                    <DefinitionTerm>{item.label}</DefinitionTerm>
                    <DefinitionValue>{item.value}</DefinitionValue>
                  </DefinitionListItem>
                ))}
              </DefinitionList>
            )}
            {customerSummaryError && (
              <Text pl={3} py={3}>
                {getErrorText(customerSummaryError)}
              </Text>
            )}
            {!isCustomerSummaryLoading &&
              !customerSummaryError &&
              !customerSummaryData && (
                <Text pl={3} py={3}>
                  No customer summary data available.
                </Text>
              )}
          </CardBody>
        </Card>
      </Box>
    </SimpleGrid>
  );
}

export default SummaryTab;
